import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const CPCCalculator = () => {
  const [totalCost, setTotalCost] = useState<string>("");
  const [clicks, setClicks] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const costNum = parseFloat(totalCost) || 0;
    const clicksNum = parseFloat(clicks) || 0;
    
    if (clicksNum === 0) {
      setResult(null);
      return;
    }

    const cpc = costNum / clicksNum;
    setResult(cpc);
  };

  const reset = () => {
    setTotalCost("");
    setClicks("");
    setResult(null);
  };

  const relatedTools = getRelatedTools("cpc-calculator");

  return (
    <ToolPageLayout
      title="CPC Calculator"
      description="Calculate your Cost Per Click to understand the efficiency of your paid advertising campaigns."
      metaDescription="CPC (Cost Per Click) measures how much you pay for each click on your ads. It's a fundamental metric for managing paid search, social, and display advertising budgets."
      icon={DollarSign}
      slug="cpc-calculator"
      formula={{
        equation: "CPC = Total Ad Spend / Number of Clicks",
        variables: [
          { name: "Total Ad Spend", description: "Total amount spent on the advertising campaign" },
          { name: "Number of Clicks", description: "Total clicks received on your ads" },
        ]
      }}
      example={{
        inputs: [
          { label: "Total Ad Spend", value: "$500" },
          { label: "Clicks", value: "250" },
        ],
        output: "$2.00 CPC",
        interpretation: "Each click costs $2. Compare this to your average conversion value to ensure profitability."
      }}
      improvements={[
        "Improve Quality Score to reduce CPC in Google Ads",
        "Use negative keywords to eliminate irrelevant clicks",
        "Adjust bids based on device, location, and time performance",
        "Test different ad formats and extensions",
        "Improve landing page relevance to boost Quality Score",
        "Use audience targeting to reach higher-intent users",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Paid Marketing", href: "/paid-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate CPC</CardTitle>
          <CardDescription>Enter your ad spend and clicks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="totalCost">Total Ad Spend ($)</Label>
            <Input
              id="totalCost"
              type="number"
              placeholder="500"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clicks">Number of Clicks</Label>
            <Input
              id="clicks"
              type="number"
              placeholder="250"
              value={clicks}
              onChange={(e) => setClicks(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate CPC
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Cost Per Click</p>
                <p className="text-4xl font-bold text-primary">
                  ${result.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default CPCCalculator;
